/**
 * PresenceIndicator - Shows which users are currently editing a request
 *
 * Displays colored avatars/dots for collaborators who are actively editing
 * the same request. Colors are deterministically assigned based on user ID.
 */

import React from 'react';
import { cn } from '../../lib/utils';
import * as Tooltip from '@radix-ui/react-tooltip';

// Generate a deterministic color from a user ID
function getUserColor(userId: string): string {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-cyan-500',
    'bg-yellow-500',
    'bg-red-500',
  ];

  // Simple hash function to get consistent color
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash << 5) - hash + userId.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return colors[Math.abs(hash) % colors.length];
}

// Get user initials from name or email
function getUserInitials(userName?: string): string {
  if (!userName) return '?';

  // Handle email format
  if (userName.includes('@')) {
    const localPart = userName.split('@')[0];
    return localPart.substring(0, 2).toUpperCase();
  }

  // Handle name format
  const parts = userName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return userName.substring(0, 2).toUpperCase();
}

interface PresenceUser {
  userId: string;
  userName?: string;
  editingRequestId?: string;
  userColor?: string;
}

interface PresenceIndicatorProps {
  users: PresenceUser[];
  size?: 'sm' | 'md' | 'lg';
  maxDisplayed?: number;
  className?: string;
}

export function PresenceIndicator({
  users,
  size = 'sm',
  maxDisplayed = 3,
  className,
}: PresenceIndicatorProps) {
  if (users.length === 0) {
    return null;
  }

  const displayedUsers = users.slice(0, maxDisplayed);
  const remainingCount = users.length - maxDisplayed;

  const sizeClasses = {
    sm: 'w-5 h-5 text-[10px]',
    md: 'w-6 h-6 text-xs',
    lg: 'w-8 h-8 text-sm',
  };

  return (
    <Tooltip.Provider delayDuration={300}>
      <div className={cn('flex items-center -space-x-1', className)}>
        {displayedUsers.map((user, index) => (
          <Tooltip.Root key={user.userId}>
            <Tooltip.Trigger asChild>
              <div
                className={cn(
                  'rounded-full flex items-center justify-center text-white font-medium ring-2 ring-background cursor-default',
                  sizeClasses[size],
                  user.userColor || getUserColor(user.userId)
                )}
                style={{ zIndex: displayedUsers.length - index }}
              >
                {getUserInitials(user.userName)}
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="z-50 px-2 py-1 text-xs bg-popover text-popover-foreground rounded shadow-md border border-border"
                sideOffset={5}
              >
                {user.userName || 'Anonymous'}
                <span className="text-muted-foreground ml-1">is editing</span>
                <Tooltip.Arrow className="fill-popover" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        ))}

        {remainingCount > 0 && (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div
                className={cn(
                  'rounded-full flex items-center justify-center text-white font-medium ring-2 ring-background bg-muted-foreground cursor-default',
                  sizeClasses[size]
                )}
              >
                +{remainingCount}
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="z-50 px-2 py-1 text-xs bg-popover text-popover-foreground rounded shadow-md border border-border"
                sideOffset={5}
              >
                {users.slice(maxDisplayed).map((u) => u.userName || 'Anonymous').join(', ')}
                <Tooltip.Arrow className="fill-popover" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        )}
      </div>
    </Tooltip.Provider>
  );
}

// Smaller dot-style indicator for compact spaces (like tabs)
interface PresenceDotProps {
  users: PresenceUser[];
  className?: string;
}

export function PresenceDot({ users, className }: PresenceDotProps) {
  if (users.length === 0) {
    return null;
  }

  // Show a single pulsing dot with the first user's color
  const firstUser = users[0];
  const color = firstUser.userColor || getUserColor(firstUser.userId);

  return (
    <Tooltip.Provider delayDuration={300}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <div
            className={cn(
              'w-2 h-2 rounded-full animate-pulse cursor-default',
              color,
              className
            )}
          />
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="z-50 px-2 py-1 text-xs bg-popover text-popover-foreground rounded shadow-md border border-border"
            sideOffset={5}
          >
            {users.length === 1 ? (
              <span>{users[0].userName || 'Someone'} is editing</span>
            ) : (
              <span>{users.length} users are editing</span>
            )}
            <Tooltip.Arrow className="fill-popover" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

// Hook to get presence users for a specific request
export function usePresenceForRequest(
  activeUsers: Map<string, PresenceUser>,
  requestId?: string | null
): PresenceUser[] {
  if (!requestId) return [];

  const usersForRequest: PresenceUser[] = [];
  activeUsers.forEach((user) => {
    if (user.editingRequestId === requestId) {
      usersForRequest.push(user);
    }
  });

  return usersForRequest;
}

// Export color generator for external use
export { getUserColor, getUserInitials };
